import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import { useSelectStyles } from '../../utils/selectStyles';
import { API_BASE_URL } from '../../config.js';
import AgentIcon from '../../components/picture-ai/AgentIcon';
import AgentParamsModal from '../../components/picture-ai/AgentParamsModal';
import AgentParamsSummary from '../../components/picture-ai/AgentParamsSummary';
import InfoHint from '../../components/picture-ai/InfoHint';
import ReplayModal from '../../components/picture-ai/ReplayModal';
import TestPictureModal from '../../components/picture-ai/TestPictureModal';
import ToggleSwitch from '../../components/picture-ai/ToggleSwitch';
import CostEstimator from '../../components/picture-ai/CostEstimator';
import { agentPresentation } from './agents';
import { buttonClass, cardClass, ghostButtonClass, inputClass, useBusyMessage, usePictureAiApi } from './usePictureAi';

const toExclusiveEnd = (date) => {
  if (!date) return null;
  const value = new Date(`${date}T00:00:00Z`);
  value.setUTCDate(value.getUTCDate() + 1);
  return value.toISOString().slice(0, 10);
};

const Section = ({ title, subtitle, children }) => (
  <section className={cardClass}>
    <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{title}</h2>
    {subtitle && <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{subtitle}</p>}
    <div className="mt-4">{children}</div>
  </section>
);

const hint = (help) => help && (
  <span className="mt-1 block text-xs text-gray-500 dark:text-gray-400">{help}</span>
);

/** A caption tied to one native control: clicking the caption focuses it. */
const Field = ({ label, help, about, children }) => (
  <label className="block">
    <span className="mb-1 flex items-center gap-1.5 text-sm font-medium text-gray-700 dark:text-gray-200">
      {label}
      {about && <InfoHint label={label}>{about}</InfoHint>}
    </span>
    {children}
    {hint(help)}
  </label>
);

/**
 * A caption for anything that is not a single native control.
 *
 * A `<label>` forwards any click inside it to its first labelable descendant, so wrapping a
 * react-select — or two controls at once — makes clicking one of them operate the other.
 */
const FieldGroup = ({ label, help, about, children }) => (
  <div className="block">
    <span className="mb-1 flex items-center gap-1.5 text-sm font-medium text-gray-700 dark:text-gray-200">
      {label}
      {about && <InfoHint label={label}>{about}</InfoHint>}
    </span>
    {children}
    {hint(help)}
  </div>
);

/**
 * Create or edit one configuration: an agent, the programs it covers, what it targets, how it runs.
 *
 * Everything above the save bar is the configuration itself, saved in one go. There is one set of
 * parameters and no version to choose: saving keeps the previous one frozen in the database so an
 * old analysis stays readable, but that is bookkeeping, not a decision to put in front of anyone.
 *
 * Writing a prompt, replaying history and trying a picture each open a panel: they need room, and
 * the screen itself should stay readable at a glance.
 */
const ConfigurationEditor = ({ fetchWithAuth }) => {
  const { t } = useTranslation();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const request = usePictureAiApi(fetchWithAuth);
  const selectStyles = useSelectStyles();
  const { busy, message, setMessage, run } = useBusyMessage();

  const isNew = id === 'new';
  const [agents, setAgents] = useState([]);
  const [models, setModels] = useState([]);
  const [projects, setProjects] = useState([]);
  const [configuration, setConfiguration] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [volume, setVolume] = useState({ monthly_average: null, months_observed: 0 });
  const [targetingValues, setTargetingValues] = useState({});
  const [suggesting, setSuggesting] = useState(false);

  const [form, setForm] = useState({
    name: '', agent_key: searchParams.get('agent') || '', projectkeys: [],
    question_scope: 'all', selected_questions: [], enabled: false,
    eligibility_ingested_after: '', transport: 'standard',
    batch_size: 25, replay_batch_size: 500, max_concurrency: 2, max_attempts: 3, max_tasks_per_run: 100,
    budget_cents_per_run: ''
  });
  const [versionForm, setVersionForm] = useState({ params: {}, provider: 'openai', model: '', version_note: '' });
  const [savedVersion, setSavedVersion] = useState(null);
  const [savedForm, setSavedForm] = useState(null);

  const [testOpen, setTestOpen] = useState(false);
  const [paramsOpen, setParamsOpen] = useState(false);
  const [replayOpen, setReplayOpen] = useState(false);

  const agent = useMemo(() => agents.find(candidate => candidate.key === form.agent_key) || null, [agents, form.agent_key]);
  const shown = useMemo(() => (agent ? agentPresentation(agent.key) : null), [agent]);

  useEffect(() => {
    Promise.all([
      request('/admin/agents'),
      request('/admin/models'),
      fetchWithAuth(`${API_BASE_URL}/api/admin/projects`).then(response => response.json())
    ]).then(([agentRows, modelRows, projectRows]) => {
      setAgents(agentRows);
      setModels(modelRows);
      setProjects(Array.isArray(projectRows) ? projectRows : []);
    }).catch(error => setMessage({ tone: 'error', text: error.message }));
  }, [request, fetchWithAuth, setMessage]);

  const loadConfiguration = useCallback(async () => {
    if (isNew) return;
    const loaded = await request(`/admin/configurations/${id}`);
    setConfiguration(loaded);
    const loadedForm = {
      name: loaded.name,
      agent_key: loaded.agent_key,
      projectkeys: loaded.programs.map(program => program.projectkey),
      question_scope: loaded.question_scope,
      selected_questions: loaded.selected_questions,
      enabled: loaded.enabled,
      eligibility_ingested_after: loaded.eligibility_ingested_after ? `${loaded.eligibility_ingested_after}`.slice(0, 10) : '',
      transport: loaded.transport,
      batch_size: loaded.batch_size,
      replay_batch_size: loaded.replay_batch_size ?? loaded.batch_size,
      max_concurrency: loaded.max_concurrency,
      max_attempts: loaded.max_attempts,
      max_tasks_per_run: loaded.max_tasks_per_run,
      budget_cents_per_run: loaded.budget_cents_per_run ?? ''
    };
    setForm(loadedForm);
    setSavedForm(loadedForm);
    const active = { params: loaded.params || {}, provider: loaded.provider || 'openai', model: loaded.model || '' };
    setVersionForm({ ...active, version_note: '' });
    setSavedVersion(active);
    setQuestions(await request(`/admin/configurations/${id}/questions`));
  }, [id, isNew, request]);

  useEffect(() => { loadConfiguration().catch(error => setMessage({ tone: 'error', text: error.message })); }, [loadConfiguration, setMessage]);

  // While drafting there is no saved program list to read from, so the questions and the volume
  // follow the current selection instead.
  useEffect(() => {
    if (form.projectkeys.length === 0) { setQuestions([]); return; }
    if (!isNew) return;
    request(`/admin/configurations/questions?projectkeys=${form.projectkeys.join(',')}`)
      .then(setQuestions)
      .catch(() => setQuestions([]));
  }, [isNew, form.projectkeys, request]);

  // The combinations restrict the volume too, but only once they fix a value: a combination still
  // being written would otherwise make the cost jump with every keystroke of an empty field.
  const targetedMatches = useMemo(() => {
    const rules = versionForm.params?.targeting?.rules || [];
    const matches = rules.map(rule => rule.match || {}).filter(match => Object.values(match).some(Boolean));
    return matches.length ? JSON.stringify(matches) : '';
  }, [versionForm.params]);

  useEffect(() => {
    if (form.projectkeys.length === 0) { setVolume({ monthly_average: null, months_observed: 0 }); return; }
    const params = new URLSearchParams({
      projectkeys: form.projectkeys.join(','),
      scope: form.question_scope,
      questions: form.selected_questions.join(',')
    });
    if (targetedMatches) params.set('targeting', targetedMatches);
    request(`/admin/configurations/volume?${params}`)
      .then(setVolume)
      .catch(() => setVolume({ monthly_average: null, months_observed: 0 }));
  }, [form.projectkeys, form.question_scope, form.selected_questions, targetedMatches, request]);

  const targetsCombinations = useMemo(
    () => (agent?.ui_descriptor?.sections || []).some(section => section.widget === 'compliance-targeting'),
    [agent]
  );

  useEffect(() => {
    if (!targetsCombinations || form.projectkeys.length === 0) { setTargetingValues({}); return; }
    const params = new URLSearchParams({
      projectkeys: form.projectkeys.join(','),
      scope: form.question_scope,
      questions: form.selected_questions.join(',')
    });
    request(`/admin/configurations/targeting-values?${params}`)
      .then(setTargetingValues)
      .catch(() => setTargetingValues({}));
  }, [targetsCombinations, form.projectkeys, form.question_scope, form.selected_questions, request]);

  useEffect(() => {
    if (agent && !versionForm.model) {
      setVersionForm(current => ({ ...current, model: agent.defaults.model, params: agent.defaults.params }));
    }
  }, [agent, versionForm.model]);

  /**
   * The questions the agent will actually see.
   *
   * Targeting a subset above and still being offered every question below was misleading: it
   * invited tuning a profile for a question the configuration never reaches.
   */
  const targetedQuestions = useMemo(() => {
    if (form.question_scope === 'all') return questions;
    const listed = question => form.selected_questions.includes(question.key)
      || (question.legacy_keys || []).some(key => form.selected_questions.includes(key));
    return questions.filter(question => listed(question) === (form.question_scope === 'selected'));
  }, [questions, form.question_scope, form.selected_questions]);

  const projectOptions = useMemo(() => {
    const byClient = new Map();
    projects.forEach((project) => {
      const client = project.client || t('Unknown client');
      byClient.set(client, [...(byClient.get(client) || []), { value: project.projectkey, label: `${project.projectname} (${project.projectkey})` }]);
    });
    return [...byClient.entries()].map(([label, options]) => ({ label, options }));
  }, [projects, t]);

  const questionOptions = questions.map(question => ({
    value: question.key,
    label: question.label,
    count: question.pictures_count,
    programs: form.projectkeys.length - (question.missing_from?.length || 0),
    totalPrograms: form.projectkeys.length
  }));

  // How many of the selected programs actually ask this question — a count reads faster than the
  // list of the ones that do not.
  const formatQuestionOption = (option) => (
    <span className="flex items-center justify-between gap-3">
      <span>{option.label} <span className="text-gray-400">({option.count})</span></span>
      {option.totalPrograms > 1 && (
        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600 dark:bg-gray-700 dark:text-gray-200">
          {option.programs}/{option.totalPrograms}
        </span>
      )}
    </span>
  );

  const versionChanged = !savedVersion
    || JSON.stringify(savedVersion) !== JSON.stringify({ params: versionForm.params, provider: versionForm.provider, model: versionForm.model });
  const formChanged = !savedForm || JSON.stringify(savedForm) !== JSON.stringify(form);
  const dirty = isNew || versionChanged || formChanged;

  const save = () => run(async () => {
    const payload = { ...form, budget_cents_per_run: form.budget_cents_per_run === '' ? null : Number(form.budget_cents_per_run) };
    const target = isNew
      ? await request('/admin/configurations', { method: 'POST', body: JSON.stringify(payload) })
      : await request(`/admin/configurations/${id}`, { method: 'PATCH', body: JSON.stringify(payload) });

    // The settings and the agent's parameters are one decision for the administrator, even though
    // the parameters are stored as an immutable version behind the scenes.
    let created = null;
    if (isNew || versionChanged) {
      created = await request(`/admin/configurations/${target.id}/versions`, { method: 'POST', body: JSON.stringify(versionForm) });
    }
    if (isNew) { navigate(`/admin/ai/configurations/${target.id}`); return { created }; }
    await loadConfiguration();
    return { created };
  }, () => t('Configuration saved.'));

  const draftCriteria = async (prompt) => {
    try {
      const result = await request('/admin/agents/compliance/criteria/suggest', {
        method: 'POST',
        body: JSON.stringify({ prompt, model: versionForm.model, provider: versionForm.provider })
      });
      setMessage({ tone: 'success', text: t('{{count}} criteria drafted. Review them before saving.', { count: result.criteria.length }) });
      return result.criteria;
    } catch (error) {
      setMessage({ tone: 'error', text: error.message });
      return null;
    }
  };

  const suggestCriteria = async () => {
    setSuggesting(true);
    try {
      const criteria = await draftCriteria(versionForm.params.prompt);
      if (criteria) setVersionForm(current => ({ ...current, params: { ...current.params, criteria } }));
    } finally {
      setSuggesting(false);
    }
  };

  const updateForm = (patch) => setForm(current => ({ ...current, ...patch }));

  return (
    <div className="space-y-5 pb-4">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          {shown && <AgentIcon agent={shown} size={44} />}
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{isNew ? t('New configuration') : form.name}</h1>
            <p className="mt-1 max-w-2xl text-sm text-gray-500 dark:text-gray-400">
              {shown ? t(shown.description) : t('Pick the agent this configuration runs.')}
            </p>
          </div>
        </div>
        <button className={ghostButtonClass} onClick={() => navigate('/admin/ai/configurations')}>{t('Back to the list')}</button>
      </header>

      <ToggleSwitch
        checked={form.enabled}
        disabled={busy || isNew}
        onChange={enabled => updateForm({ enabled })}
        label={form.enabled ? t('Enabled') : t('Disabled')}
        description={isNew
          ? t('Save the configuration first: it can only be enabled once it has parameters to run.')
          : (form.enabled
            ? t('Every new picture matching the scope is analyzed automatically, and billed.')
            : t('Nothing is analyzed automatically. Testing on one picture still works.'))}
      />

      <Section title={t('Configuration')}>
        <div className="grid gap-4 lg:grid-cols-2">
          <Field label={t('Name')}>
            <input className={inputClass} value={form.name} onChange={event => updateForm({ name: event.target.value })} />
          </Field>

          <Field
            label={t('Agent')}
            about={!isNew ? t('The agent cannot change: past analyses were produced by it.') : undefined}
          >
            <select className={inputClass} value={form.agent_key} disabled={!isNew} onChange={event => updateForm({ agent_key: event.target.value })}>
              <option value="">{t('Pick an agent')}</option>
              {agents.map((candidate) => {
                const presentation = agentPresentation(candidate.key);
                return <option key={candidate.key} value={candidate.key}>{t(presentation.name)} — {t(presentation.tagline)}</option>;
              })}
            </select>
          </Field>

          {shown && (
            <div className="rounded-xl border border-gray-200 p-4 dark:border-gray-700 lg:col-span-2" style={{ borderLeftWidth: 4, borderLeftColor: shown.color }}>
              <div className="grid gap-4 md:grid-cols-3">
                {[[t('What it returns'), shown.outputs], [t('Use it for'), shown.goodFor], [t('Not for'), shown.notFor]].map(([title, items]) => (
                  <div key={title}>
                    <div className="text-xs font-semibold uppercase text-gray-500">{title}</div>
                    <ul className="mt-1 list-inside list-disc text-xs leading-5 text-gray-600 dark:text-gray-300">
                      {items.map(item => <li key={item}>{t(item)}</li>)}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </Section>

      <Section title={t('Scope')} subtitle={t('The programs this configuration covers, and the questions it targets within them.')}>
        <div className="space-y-4">
          <FieldGroup label={t('Programs')}>
            <Select
              isMulti options={projectOptions} styles={selectStyles}
              value={form.projectkeys.map(projectkey => ({ value: projectkey, label: projectkey }))}
              onChange={options => updateForm({ projectkeys: (options || []).map(option => option.value) })}
            />
          </FieldGroup>

          <Field label={t('Targeted questions')}>
            <select className={inputClass} value={form.question_scope} onChange={event => updateForm({ question_scope: event.target.value })}>
              <option value="all">{t('All questions of these programs')}</option>
              <option value="selected">{t('Only the questions below')}</option>
              <option value="excluded">{t('All questions except the ones below')}</option>
            </select>
          </Field>

          {form.question_scope === 'excluded' && (
            <p className="text-xs text-amber-700 dark:text-amber-300">
              {t('Questions that appear later in these programs will be analyzed too, and billed.')}
            </p>
          )}

          {form.question_scope !== 'all' && (
            <Select
              isMulti options={questionOptions} styles={selectStyles} formatOptionLabel={formatQuestionOption}
              value={form.selected_questions.map(key => questionOptions.find(option => option.value === key) || { value: key, label: key })}
              onChange={options => updateForm({ selected_questions: (options || []).map(option => option.value) })}
            />
          )}
        </div>
      </Section>

      <Section title={t('Model and cost')}>
        <CostEstimator
          model={versionForm.model}
          models={models}
          provider={versionForm.provider}
          onModelChange={model => setVersionForm(current => ({ ...current, model }))}
          onProviderChange={provider => setVersionForm(current => ({ ...current, provider }))}
          monthlyVolume={volume.monthly_average}
          monthsObserved={volume.months_observed}
          transport={form.transport}
          onTransportChange={transport => updateForm({ transport })}
          usageEstimate={agent?.defaults?.usage_estimate}
          observedInputTokens={configuration?.average_input_tokens ?? null}
          observedOutputTokens={configuration?.average_output_tokens ?? null}
          pricedAnalyses={configuration?.priced_analyses || 0}
          budgetCents={form.budget_cents_per_run}
          onBudgetChange={value => updateForm({ budget_cents_per_run: value })}
          disabled={busy}
        />
      </Section>

      {agent && (
        <Section title={t('Agent parameters')}>
          <div className="space-y-3">
            <AgentParamsSummary agent={agent} params={versionForm.params} />
            <button className={ghostButtonClass} onClick={() => setParamsOpen(true)}>{t('Edit the parameters')}</button>
          </div>
        </Section>
      )}

      <Section title={t('Execution')}>
        <div className="grid gap-4 lg:grid-cols-4">
          <Field label={t('Analyze pictures ingested after')}>
            <input type="date" className={inputClass} value={form.eligibility_ingested_after} onChange={event => updateForm({ eligibility_ingested_after: event.target.value })} />
          </Field>
          <Field label={t('Max tasks per cycle')}>
            <input type="number" min="1" max="5000" className={inputClass} value={form.max_tasks_per_run} onChange={event => updateForm({ max_tasks_per_run: Number(event.target.value) })} />
          </Field>
          <Field
            label={t('Max analyses in parallel')}
            about={t('For this configuration, all programs together — it protects the provider quota, which is global.')}
          >
            <input type="number" min="1" max="20" className={inputClass} value={form.max_concurrency} onChange={event => updateForm({ max_concurrency: Number(event.target.value) })} />
          </Field>
          <Field
            label={t('Batch size, incoming')}
            about={t('How many pictures go into one provider batch for the continuous flow. A few dozen arrive per hour, so a large size only makes them wait for each other. Between 1 and 500.')}
          >
            <input
              type="number" min="1" max="500" className={inputClass} value={form.batch_size}
              onChange={event => updateForm({ batch_size: Number(event.target.value) })}
            />
          </Field>
          <Field
            label={t('Batch size, replay')}
            about={t('Same thing for a replay, which always travels in batch and enqueues tens of thousands at once. A large size means fewer batches to submit and to wait on. Between 1 and 500.')}
          >
            <input
              type="number" min="1" max="500" className={inputClass} value={form.replay_batch_size}
              onChange={event => updateForm({ replay_batch_size: Number(event.target.value) })}
            />
          </Field>
          <Field
            label={t('Attempts before giving up')}
            about={t('A picture is retried this many times before it is marked failed. Each retry costs another call.')}
          >
            <input
              type="number" min="1" max="10" className={inputClass} value={form.max_attempts}
              onChange={event => updateForm({ max_attempts: Number(event.target.value) })}
            />
          </Field>
        </div>
      </Section>

      {/* One save, at the bottom, for everything above it. */}
      <div className="sticky bottom-0 z-10 -mx-1 flex flex-wrap items-center justify-between gap-3 rounded-t-2xl border-t border-gray-200 bg-white/95 px-4 py-3 backdrop-blur dark:border-gray-700 dark:bg-gray-900/95">
        <span className={`text-sm ${message?.tone === 'error' ? 'text-red-700 dark:text-red-300' : 'text-green-700 dark:text-green-300'}`}>
          {message?.text}
          {!message && dirty && !isNew && (
            <span className="text-gray-500 dark:text-gray-400">{t('Unsaved changes.')}</span>
          )}
        </span>
        <button className={buttonClass} disabled={busy || !dirty || !form.name || !form.agent_key} onClick={save}>
          {isNew ? t('Create the configuration') : t('Save')}
        </button>
      </div>

      {!isNew && (
        <>
          <Section title={t('Test on one picture')} subtitle={t('Find a picture, look at it, then run the active version on it.')}>
            <button className={buttonClass} disabled={!configuration?.active_version_id} onClick={() => setTestOpen(true)}>
              {t('Open the test')}
            </button>
            {!configuration?.active_version_id && (
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">{t('Save the configuration first: there is no version to run yet.')}</p>
            )}
          </Section>

          <Section title={t('Replay history')} subtitle={t('Re-run this configuration over pictures already in Gallery.')}>
            <button className={buttonClass} disabled={!configuration?.active_version_id} onClick={() => setReplayOpen(true)}>
              {t('Open the replay panel')}
            </button>
          </Section>
        </>
      )}
      <AgentParamsModal
        open={paramsOpen}
        onClose={() => setParamsOpen(false)}
        onSave={save}
        agent={agent}
        params={versionForm.params}
        onChange={params => setVersionForm(current => ({ ...current, params }))}
        busy={busy}
        widgetContext={{
          questions: targetedQuestions,
          onSuggest: agent?.key === 'compliance' ? suggestCriteria : undefined,
          onDraftCriteria: agent?.key === 'compliance' ? draftCriteria : undefined,
          targetingValues,
          suggesting
        }}
      />

      <ReplayModal
        open={replayOpen}
        onClose={() => setReplayOpen(false)}
        configuration={configuration}
        request={request}
      />

      <TestPictureModal
        open={testOpen}
        onClose={() => setTestOpen(false)}
        configuration={configuration}
        agent={agent}
        params={versionForm.params}
        models={models}
        pendingModel={versionForm.model}
        fetchWithAuth={fetchWithAuth}
        request={request}
      />
    </div>
  );
};

export default ConfigurationEditor;
